import { z } from 'zod';

/** Chat contracts (spec §6.7). Phase 1: global and proximity; direct messages arrive in Phase 2. */

export const CHAT_MAX_LENGTH = 500;
export const CHAT_CHANNELS = ['global', 'proximity'] as const;
export type ChatChannel = (typeof CHAT_CHANNELS)[number];

/** Number of global messages replayed to a player on join. */
export const CHAT_HISTORY_ON_JOIN = 50;

/**
 * Normalises a chat line into safe plain text: control characters removed, whitespace runs
 * collapsed, ends trimmed. Rendering escapes HTML anyway (React); this keeps stored data clean.
 */
export function normalizeChatBody(raw: string): string {
  return raw
    .replace(/\p{Cc}+/gu, ' ') // control characters, including newlines and tabs
    .replace(/\s+/gu, ' ')
    .trim();
}

/** Client → server. The body is normalised and length-checked *after* trimming. */
export const chatSendSchema = z
  .object({
    channel: z.enum(CHAT_CHANNELS),
    body: z
      .string()
      .max(CHAT_MAX_LENGTH * 4) // cheap upper bound before normalising
      .transform(normalizeChatBody)
      .pipe(z.string().min(1).max(CHAT_MAX_LENGTH)),
  })
  .strict();
export type ChatSend = z.infer<typeof chatSendSchema>;

/** Server → client. */
export interface ChatEvent {
  readonly id: string;
  readonly channel: ChatChannel;
  readonly senderId: string;
  readonly senderName: string;
  /** Colyseus session id of the sender when online: lets clients draw a speech bubble. */
  readonly senderSessionId: string | null;
  readonly body: string;
  /** Epoch milliseconds. */
  readonly at: number;
}
